'use client';
import {
  Archive,
  AlertTriangle,
  Bell,
  Eye,
  Mail,
  MessageSquareText,
  ScanSearch,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  NotificationStatus,
  NotificationType,
  RemoteTaskManagerProvider,
} from '@prisma/client';
import { useRouter } from 'next/navigation';
import HoverClickPopout from '~/components/HoverClickPopout';
import usePopover from '~/hooks/usePopover';
import { type RouterOutputs, api } from '~/utils/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);
import { useState, useEffect, type MouseEvent } from 'react';
import { serverError } from '~/utils/serverError';
import { getNotificationNavigation } from '~/utils/getNotificationNavigation';
import Link from 'next/link';
import { Routes } from '~/config';
import { capitalCase } from 'change-case';
import { cn } from '~/lib/utils';
import NavBarItem from './NavBarItem';
import {
  NavPopoverBody,
  NavPopoverFooter,
  NavPopoverHeader,
  NavPopoverShell,
  navPopoverFooterLinkClass,
  navPopoverIconButtonClass,
  navPopoverListItemClass,
} from './NavPopoverShell';

type TNotification = RouterOutputs['notifications']['getNotifications']['notifications'][0];

function notificationIcon(type: NotificationType): LucideIcon {
  switch (type) {
    case NotificationType.SCANNER_RUN_COMPLETED:
      return ScanSearch;
    case NotificationType.FEEDBACK_ANNOTATION_CREATED:
      return MessageSquareText;
    case NotificationType.DAILY_DIGEST:
      return Mail;
    default:
      return Bell;
  }
}

type NotificationProps = {
  notification: TNotification;
  onSuccessUpdate: () => Promise<any>;
  onClose: () => void;
  workspaceId?: string;
};

const Notification = (props: NotificationProps) => {
  const router = useRouter();
  const { notification, onClose, onSuccessUpdate, workspaceId } = props;
  const { mutateAsync: updateNotifications } = api.notifications.updateNotifications.useMutation();
  const [isArchiving, setIsArchiving] = useState(false);
  const [isMarkingAsRead, setIsMarkingAsRead] = useState(false);

  const updateNotificationStatus = async (
    status: keyof Pick<typeof NotificationStatus, 'ARCHIVED' | 'READ'>,
  ) => {
    await updateNotifications({
      notificationIds: [notification.id],
      status,
    });
    await onSuccessUpdate();
  };

  const handleNotificationClick = async () => {
    updateNotificationStatus(NotificationStatus.READ).catch((e) => serverError(e as Error));
    const navigationPath = getNotificationNavigation(notification, workspaceId);
    navigationPath && router.push(navigationPath);
    onClose();
  };

  const handleArchiveNotification = async (event: MouseEvent) => {
    try {
      setIsArchiving(true);
      event.stopPropagation();
      await updateNotificationStatus(NotificationStatus.ARCHIVED);
    } catch (e) {
      serverError(e as Error);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleMarkAsRead = async (event: MouseEvent) => {
    try {
      setIsMarkingAsRead(true);
      event.stopPropagation();
      await updateNotificationStatus(NotificationStatus.READ);
    } catch (e) {
      serverError(e as Error);
    } finally {
      setIsMarkingAsRead(false);
    }
  };

  const isUnread = notification.status === NotificationStatus.UNREAD;
  const Icon = notificationIcon(notification.type);

  return (
    <div
      className={navPopoverListItemClass}
      onClick={handleNotificationClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          void handleNotificationClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <span className="bg-muted text-muted-foreground mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md ring-1 ring-border/60">
        <Icon className="size-3.5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1 pr-8">
        <div className="flex items-center gap-2">
          <h4
            className={cn(
              'text-foreground truncate leading-tight',
              isUnread ? 'font-medium' : 'font-normal',
            )}
          >
            {notification.title}
          </h4>
          {isUnread ? (
            <span
              className="bg-foreground size-1.5 shrink-0 rounded-full group-hover:opacity-0 group-focus-within:opacity-0"
              aria-label="Unread"
            />
          ) : null}
        </div>
        <p className="text-muted-foreground mt-0.5 line-clamp-2 text-[11px] leading-snug">
          {notification.message}
        </p>
        <p className="text-muted-foreground mt-1.5 text-[11px] tabular-nums">
          {dayjs(notification.createdAt).fromNow()}
        </p>
      </div>

      <div
        className={cn(
          'absolute top-2.5 right-2 flex items-center gap-0.5',
          'opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100',
        )}
      >
        {isUnread ? (
          <button
            type="button"
            title="Mark as read"
            aria-label="Mark as read"
            className={navPopoverIconButtonClass}
            onClick={handleMarkAsRead}
            disabled={isMarkingAsRead}
          >
            <Eye className="size-3.5" />
          </button>
        ) : null}

        <button
          type="button"
          title="Archive"
          aria-label="Archive"
          className={cn(navPopoverIconButtonClass, 'hover:text-destructive')}
          onClick={handleArchiveNotification}
          disabled={isArchiving}
        >
          <Archive className="size-3.5" />
        </button>
      </div>
    </div>
  );
};

type NotificationsProps = {
  isSidebarExpanded: boolean;
  onGetUnreadCount?: (count: number) => void;
  workspaceId?: string;
};

export default function Notifications({
  isSidebarExpanded,
  onGetUnreadCount,
  workspaceId,
}: NotificationsProps) {
  const { data: allIntegrationMetadata } = api.oAuth.getAllIntegrationMetadata.useQuery(undefined, {
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const { open, handleClose, handleOpen, anchorEl } = usePopover<HTMLDivElement>();

  const { data: notificationData, refetch } = api.notifications.getNotifications.useQuery(
    {
      limit: 50,
      offset: 0,
    },
    {
      staleTime: 0,
      refetchOnWindowFocus: true,
    },
  );

  const invalidIntegrations = allIntegrationMetadata?.filter((i) => i.isInvalid);

  const notifications = notificationData?.notifications || [];
  const unreadCount = (notificationData?.totalUnread || 0) + (invalidIntegrations?.length || 0);

  useEffect(() => {
    if (onGetUnreadCount) {
      onGetUnreadCount(unreadCount);
    }
  }, [unreadCount, onGetUnreadCount]);

  const renderNotificationsContent = (closePopout: () => void) => {
    const allHref = `${workspaceId ? `/${workspaceId}` : ''}${Routes.NOTIFICATIONS}/activity`;

    return (
      <NavPopoverShell width={360}>
        <NavPopoverHeader
          title="Inbox"
          meta={
            unreadCount > 0 ? (
              <span className="bg-muted text-muted-foreground rounded-md px-1.5 py-0.5 text-[11px] font-medium tabular-nums">
                {unreadCount} new
              </span>
            ) : undefined
          }
          actions={
            <button
              type="button"
              className={navPopoverIconButtonClass}
              onClick={closePopout}
              aria-label="Close"
            >
              <X className="size-3.5" />
            </button>
          }
        />

        <NavPopoverBody className="max-h-[min(28rem,60vh)]">
          {!notifications.length && !invalidIntegrations?.length ? (
            <div className="flex flex-col items-center justify-center px-6 py-14">
              <div className="bg-muted mb-3 flex size-10 items-center justify-center rounded-[10px] ring-1 ring-border/60">
                <Bell className="text-muted-foreground size-5" />
              </div>
              <p className="text-foreground text-sm font-medium">You&apos;re all caught up</p>
              <p className="text-muted-foreground mt-1 text-center text-[11px]">
                No new notifications
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/80">
              {!!invalidIntegrations?.length &&
                invalidIntegrations.map((i) => {
                  const href = (Object.values(RemoteTaskManagerProvider) as string[]).includes(
                    i.provider,
                  )
                    ? `${workspaceId ? `/${workspaceId}` : ''}${Routes.SETTINGS}/review-queues`
                    : `${workspaceId ? `/${workspaceId}` : ''}${Routes.SETTINGS}/file-access`;

                  return (
                    <Link
                      key={i.id}
                      href={href}
                      onClick={() => closePopout()}
                      className={navPopoverListItemClass}
                    >
                      <span className="bg-destructive/10 text-destructive mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md ring-1 ring-destructive/20">
                        <AlertTriangle className="size-3.5" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="text-foreground block font-medium leading-tight">
                          {capitalCase(i.provider)}
                        </span>
                        <span className="text-muted-foreground mt-0.5 block text-[11px] leading-snug">
                          Session is invalid. Sign in again to restore access.
                        </span>
                      </span>
                    </Link>
                  );
                })}
              {notifications.map((notification) => (
                <Notification
                  key={notification.id}
                  notification={notification}
                  onSuccessUpdate={refetch}
                  onClose={closePopout}
                  workspaceId={workspaceId}
                />
              ))}
            </div>
          )}
        </NavPopoverBody>

        <NavPopoverFooter>
          <Link href={allHref} onClick={closePopout} className={navPopoverFooterLinkClass}>
            View all notifications
          </Link>
        </NavPopoverFooter>
      </NavPopoverShell>
    );
  };

  return (
    <HoverClickPopout
      renderContent={renderNotificationsContent}
      isOpen={open}
      anchorEl={anchorEl}
      onOpen={handleOpen}
      onClose={handleClose}
      width="360px"
      fullWidth
      openOnHover={false}
      offset={0}
      alignToContentPaneBottom
    >
      <NavBarItem
        label="Notifications"
        icon={Bell}
        isSidebarExpanded={isSidebarExpanded}
        asButton
        disableTooltip={isSidebarExpanded}
        badgeCount={unreadCount}
        badge={
          unreadCount > 0 ? (
            <span className="text-[11px] font-medium text-muted-foreground tabular-nums">
              {unreadCount > 999 ? '999+' : unreadCount}
            </span>
          ) : undefined
        }
      />
    </HoverClickPopout>
  );
}
